import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Tabs,
  Tab,
  Form,
  Row,
  Col,
  Card,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaQuestionCircle,
  FaLayerGroup,
  FaBell,
  FaSearch,
  FaPaintBrush,
  FaBuilding,
  FaComments,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import InputColor from "react-input-color";
import axios from "axios";
import { Calendar, Users, UploadCloud, Trash2 } from "react-feather";
import { useNotification } from "../../context/NotificationContext";
import PropertyInfo from "./components/PropertyInfo";
import NotificationSettings from "./components/NotificationSettingsEditor";
import SeoEditor from "./components/SeoEditor";
import AiBotSettingsEditor from "./components/AiBotSettingsEditor";
import ManageFaqs from "./components/ManageFaqs";

const ManageBookingEngine = () => {
  const [activeKey, setActiveKey] = useState("layout");
  const [isEnabled, setIsEnabled] = useState(false);
  const [hasConfig, setHasConfig] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [heroImageKey, setHeroImageKey] = useState("");
  const { notify } = useNotification();
  const tabsRailRef = useRef(null);

  const apiUrl =
    process.env.REACT_APP_API_URL || "http://localhost:3000/api/hms";
  const CDN_URL = (process.env.REACT_APP_CDN_URL || "").replace(/\/$/, "");
  const token = localStorage.getItem("token");

  const [galleryImages, setGalleryImages] = useState([]);
  const [subdomain, setSubdomain] = useState("");

  const authHeaders = {
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      "Content-Type": "application/json",
    },
  };

  const MIN_WIDTH = 1200;
  const MIN_HEIGHT = 600;
  const MAX_FILE_BYTES = 2 * 1024 * 1024;

  const LOGO_WIDTH_MIN = 80;
  const LOGO_WIDTH_MAX = 400;
  const LOGO_HEIGHT_MIN = 40;
  const LOGO_HEIGHT_MAX = 260;

  const defaultBranding = {
    title: "Book Your Stay",
    subtitle: "Find your perfect room",
    titleColor: "#ffffff",
    titleSize: 2.5,
    subtitleColor: "#eaeaea",
    subtitleSize: 1.2,
    backgroundType: "image",
    backgroundValue: "",
    imageOpacity: 0.4,
    gradientStart: "#007bff",
    gradientEnd: "#00c6ff",
    themeColor: "#1c1b1f",
    searchStyle: "rounded",
    buttonText: "Search Availability",
    widgetType: "popup",

    showLogo: false,
    logoUrl: "",
    lightLogoUrl: "",
    logoWidth: 230,
    logoHeight: 180,

    showPropertyInfo: true,
    propertyName: "Hotel Sunshine",
    propertyOverview:
      "The Acoma House is centrally located on the Avenue of the Arts in the Golden Triangle district of Denver...",

    seo: {},
    aiBot: {
      enabled: false,
      assistantNames: "",
      chatBubbleName: "",
      showQuickActions: true,
      quickActions: {
        availability: true,
        book: true,
        myBookings: true,
        modify: true,
        cancel: true,
        offers: true,
        eventsWedding: true,
      },
      trainingData: "",
    },
  };

  const [branding, setBranding] = useState(defaultBranding);

  useEffect(() => {
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchConfig = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `${apiUrl}/booking-engine/config`,
        authHeaders
      );

      if (response.data) {
        setHasConfig(true);

        const rawCfg = response.data.config || {};
        const cfg = { ...defaultBranding, ...rawCfg };

        if (cfg.backgroundType === "image") cfg.backgroundValue = "";
        delete cfg.subdomain;

        const fetchedSeo = response.data.seo || {};
        setBranding({ ...cfg, seo: fetchedSeo });

        const heroKey =
          response.data.hero_image ||
          response.data.heroImage ||
          response.data.config?.hero_image ||
          response.data.hero_image_key ||
          "";

        setHeroImageKey(heroKey || "");
        setGalleryImages(response.data.gallery_images || []);
        setSubdomain(response.data.subdomain || "");

        const active =
          typeof response.data.is_active === "boolean"
            ? response.data.is_active
            : true;
        setIsEnabled(active);
      } else {
        setHasConfig(false);
        setIsEnabled(false);
      }
    } catch (error) {
      console.error("Error fetching config:", error);
      if (error.response?.status === 404) {
        setHasConfig(false);
        setIsEnabled(false);
      } else {
        notify("error", "Failed to load config. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnableToggle = (value) => {
    const enabled = value === 1 || value === true;
    setIsEnabled(enabled);

    if (enabled && !hasConfig) {
      setBranding((prev) => ({
        ...defaultBranding,
        ...prev,
        seo: prev.seo || {},
      }));
      setHeroImageKey("");
      setGalleryImages([]);
      setSubdomain("");
    }
  };

  const handleSave = async () => {
    if (!isEnabled) {
      notify("warning", "Enable Booking Engine first to save changes.");
      return;
    }

    if (!subdomain || !subdomain.trim()) {
      notify(
        "error",
        "Subdomain is required. Please set it in the SEO Settings tab."
      );
      setActiveKey("seo");
      return;
    }

    if (branding.showLogo) {
      const w = parseInt(branding.logoWidth, 10);
      const h = parseInt(branding.logoHeight, 10);

      if (!w || Number.isNaN(w) || w < LOGO_WIDTH_MIN || w > LOGO_WIDTH_MAX) {
        notify(
          "error",
          `Logo width must be between ${LOGO_WIDTH_MIN} and ${LOGO_WIDTH_MAX} px.`
        );
        setActiveKey("layout");
        return;
      }

      if (!h || Number.isNaN(h) || h < LOGO_HEIGHT_MIN || h > LOGO_HEIGHT_MAX) {
        notify(
          "error",
          `Logo height must be between ${LOGO_HEIGHT_MIN} and ${LOGO_HEIGHT_MAX} px.`
        );
        setActiveKey("layout");
        return;
      }
    }

    const aiBot = branding.aiBot || defaultBranding.aiBot;
    if (aiBot?.enabled) {
      const names = String(aiBot.assistantNames || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      if (names.length === 0) {
        notify(
          "error",
          "AI Assistant Name is required (comma separated). Example: Jiya, Meacy"
        );
        setActiveKey("ai-concierge");
        return;
      }

      if (!String(aiBot.chatBubbleName || "").trim()) {
        notify(
          "error",
          'ChatBubble Name is required. Example: "Talk with Front Office"'
        );
        setActiveKey("ai-concierge");
        return;
      }

      const words = String(aiBot.trainingData || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (words.length > 300) {
        notify(
          "error",
          `BOT Training Data must be 300 words or less (currently ${words.length}).`
        );
        setActiveKey("ai-concierge");
        return;
      }

      if (aiBot.showQuickActions) {
        const qa = aiBot.quickActions || {};
        if (!Object.values(qa).some(Boolean)) {
          notify(
            "error",
            "Select at least one BOT option (or disable Bot Options)."
          );
          setActiveKey("ai-concierge");
          return;
        }
      }
    }

    setSaving(true);
    try {
      const { seo, ...configWithoutSeo } = branding;
      delete configWithoutSeo.subdomain;

      const cfgToSave = { ...configWithoutSeo };

      if (cfgToSave.backgroundType === "image") {
        cfgToSave.backgroundValue = "";
      }

      const normalizedOpacity = Math.min(
        Math.max(parseFloat(cfgToSave.imageOpacity ?? 0.4), 0),
        1
      );
      cfgToSave.imageOpacity = normalizedOpacity;

      const payload = {
        config: cfgToSave,
        is_active: isEnabled,
        config_name: hasConfig
          ? "Booking Engine Settings"
          : "Default Booking Engine Config",
        seo: seo || {},
        subdomain: subdomain.trim(),
      };

      const response = await axios.post(
        `${apiUrl}/booking-engine/config`,
        payload,
        authHeaders
      );

      const rawSavedCfg = response.data.config || cfgToSave;
      const savedCfg = { ...defaultBranding, ...rawSavedCfg };

      if (savedCfg.backgroundType === "image") savedCfg.backgroundValue = "";
      delete savedCfg.subdomain;

      const savedSeo = response.data.seo || seo;
      setBranding({ ...savedCfg, seo: savedSeo });

      setSubdomain(response.data.subdomain || subdomain.trim());
      if (typeof response.data.is_active === "boolean") {
        setIsEnabled(response.data.is_active);
      }

      const heroKey = response.data.hero_image || "";
      if (heroKey) setHeroImageKey(heroKey);

      setHasConfig(true);
      notify("success", "Settings saved successfully!");
    } catch (error) {
      console.error("Error saving:", error);
      notify("error", "Failed to save settings. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleColorChange = (field, color) => {
    setBranding((prev) => ({ ...prev, [field]: color.hex }));
  };

  const handleRangeChange = (field, e) => {
    setBranding((prev) => ({
      ...prev,
      [field]: parseFloat(e.target.value),
    }));
  };

  const handleNumericChange = (field, min, max) => (e) => {
    let value = e.target.value;
    if (value === "") {
      setBranding((prev) => ({ ...prev, [field]: "" }));
      return;
    }
    let num = parseInt(value, 10);
    if (Number.isNaN(num)) num = min;
    if (typeof min === "number") num = Math.max(min, num);
    if (typeof max === "number") num = Math.min(max, num);
    setBranding((prev) => ({ ...prev, [field]: num }));
  };

  const validateImageFile = (file) =>
    new Promise((resolve, reject) => {
      if (!file) return reject(new Error("No file provided"));
      if (file.size > MAX_FILE_BYTES)
        return reject(new Error("File size must be 2MB or less"));

      const url = URL.createObjectURL(file);
      const img = new Image();

      img.onload = function () {
        URL.revokeObjectURL(url);
        if (img.width < MIN_WIDTH || img.height < MIN_HEIGHT) {
          // relaxed by design
        }
        resolve({ width: img.width, height: img.height });
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Invalid image file"));
      };

      img.src = url;
    });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      await validateImageFile(file);

      const formData = new FormData();
      formData.append("image", file);

      const response = await axios.post(
        `${apiUrl}/booking-engine/upload-image`,
        formData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : "",
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data?.url) {
        const fileKey =
          response.data.fileKey ||
          response.data.key ||
          (response.data.url && response.data.url.replace(`${CDN_URL}/`, ""));

        setHeroImageKey(fileKey || "");
        setBranding((prev) => ({ ...prev, backgroundValue: "" }));
        notify("success", "Background image uploaded successfully!");
      } else {
        notify("error", "Upload failed. Try again.");
      }
    } catch (err) {
      console.error("Image upload error:", err);
      notify(
        "error",
        err.message ||
          "Image upload failed. Ensure image meets requirements."
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveHeroImage = async () => {
    if (!heroImageKey) {
      notify("warning", "No image to remove");
      return;
    }

    setUploading(true);
    try {
      await axios.delete(`${apiUrl}/booking-engine/hero-image`, {
        data: { heroImageKey },
        ...authHeaders,
      });
      setHeroImageKey("");
      notify("success", "Hero image removed successfully!");
    } catch (error) {
      console.error("Error removing image:", error);
      notify("error", "Failed to remove image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const getBackground = () => {
    const rawOpacity = branding.imageOpacity ?? 0.4;
    const opacity = Math.min(Math.max(parseFloat(rawOpacity) || 0.4, 0), 1);
    const overlay = `rgba(0,0,0,${1 - opacity})`;

    if (branding.backgroundType === "image") {
      const heroUrl = heroImageKey
        ? `${CDN_URL}/${heroImageKey}`
        : branding.backgroundValue || "";

      return {
        backgroundImage: heroUrl
          ? `linear-gradient(${overlay}, ${overlay}), url(${heroUrl})`
          : `linear-gradient(${overlay}, ${overlay})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      };
    }

    if (branding.backgroundType === "gradient") {
      return {
        background: `linear-gradient(45deg, ${branding.gradientStart}, ${branding.gradientEnd})`,
      };
    }

    return { background: branding.backgroundValue || "#ffffff" };
  };

  const renderImagePreviewControls = () => {
    return (
      <div className="preview-actions">
        <label style={{ cursor: "pointer", marginBottom: 0 }}>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
          />
          <OverlayTrigger overlay={<Tooltip>Upload hero image</Tooltip>}>
            <div className="preview-icon-btn" title="Upload">
              <UploadCloud size={15} />
            </div>
          </OverlayTrigger>
        </label>

        {heroImageKey && (
          <OverlayTrigger overlay={<Tooltip>Remove hero image</Tooltip>}>
            <div
              className="preview-icon-btn"
              style={{ cursor: "pointer" }}
              onClick={handleRemoveHeroImage}
              title="Remove"
            >
              <Trash2 size={15} />
            </div>
          </OverlayTrigger>
        )}
      </div>
    );
  };

  const scrollTabs = (direction) => {
    if (!tabsRailRef.current) return;
    tabsRailRef.current.scrollBy({
      left: direction === "left" ? -220 : 220,
      behavior: "smooth",
    });
  };

  const previewScale = useMemo(() => {
    const logoWidth = Math.max(72, Math.min(branding.logoWidth || 230, 240));
    const logoHeight = Math.max(34, Math.min(branding.logoHeight || 180, 110));

    return {
      logoWidth: Math.round(logoWidth * 0.46),
      logoHeight: Math.round(logoHeight * 0.46),
      titleSize: Math.max(
        1.08,
        Math.min((branding.titleSize || 2.5) * 0.64, 2.1)
      ),
      subtitleSize: Math.max(
        0.78,
        Math.min((branding.subtitleSize || 1.2) * 0.7, 1.1)
      ),
    };
  }, [
    branding.logoWidth,
    branding.logoHeight,
    branding.titleSize,
    branding.subtitleSize,
  ]);

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "50vh" }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-engine-admin-shell">
      <style>{pageStyles}</style>

      <div className="container-fluid booking-admin-container">
        <div className="page-head-wrap">
          <div>
            <div className="page-kicker">Booking Engine Administration</div>
            <h3 className="page-title">
              <i className="fas fa-cog text-primary me-2" />
              Manage Booking Engine
            </h3>
            <div className="page-subtitle">
              Configure presentation, property content, SEO, automation and AI
              experience from one place.
            </div>
          </div>

          <div className="head-actions">
            <StatusPill
              tone={isEnabled ? "success" : "warning"}
              text={isEnabled ? "Enabled" : "Disabled"}
            />
            <Button
              variant="primary"
              className="head-save-btn"
              onClick={handleSave}
              disabled={!isEnabled || saving || uploading}
            >
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </div>

        {!isEnabled ? (
          <Alert className="enterprise-alert enterprise-alert-warning">
            <div className="alert-content-block">
              <div>
                <div className="alert-title">Booking Engine is disabled</div>
                <div className="alert-subtitle">
                  {hasConfig
                    ? "Configuration already exists. Re-enable and save to make it active."
                    : "Enable the booking engine to start customization and launch setup."}
                </div>
              </div>

              <ToggleButtonGroup
                type="radio"
                name="enableToggle"
                value={isEnabled ? 1 : 0}
                onChange={handleEnableToggle}
              >
                <ToggleButton
                  id="disable"
                  value={0}
                  variant="outline-warning"
                  disabled={saving}
                >
                  Disabled
                </ToggleButton>
                <ToggleButton
                  id="enable"
                  value={1}
                  variant="success"
                  disabled={saving}
                >
                  Enable
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </Alert>
        ) : (
          <Alert className="enterprise-alert enterprise-alert-success">
            <div className="alert-content-block">
              <div>
                <div className="alert-title">Booking Engine is enabled</div>
                <div className="alert-subtitle">
                  Your settings are active. Refine the experience below and save
                  whenever needed.
                </div>
              </div>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <StatusPill
                  tone={hasConfig ? "info" : "warning"}
                  text={hasConfig ? "Config exists" : "New config"}
                />
                <Button
                  variant="primary"
                  onClick={handleSave}
                  disabled={saving || uploading}
                >
                  {saving ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Saving...
                    </>
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>
          </Alert>
        )}

        {!isEnabled ? (
          <div className="empty-state-card">
            <div className="empty-state-icon">
              <FaLayerGroup />
            </div>
            <h5 className="mb-2">Enable Booking Engine to begin</h5>
            <p className="text-muted mb-0">
              Once enabled, you can manage page appearance, property content,
              AI concierge, SEO, notifications and preview the guest-facing
              experience.
            </p>
          </div>
        ) : (
          <Row className="g-4">
            <Col xl={activeKey === "layout" ? 8 : 12}>
              <Card className="editor-shell-card border-0">
                <Card.Body className="p-0">
                  <div className="editor-top-tabs">
                    <div className="tabs-arrow-wrap">
                      <Button
                        variant="light"
                        className="tabs-arrow-btn"
                        onClick={() => scrollTabs("left")}
                      >
                        <FaChevronLeft />
                      </Button>
                    </div>

                    <div className="tabs-scroll-region" ref={tabsRailRef}>
                      <div className="enterprise-tabs">
                        <Tabs
                          activeKey={activeKey}
                          onSelect={(k) => setActiveKey(k)}
                          className="mb-0 flex-nowrap"
                        >
                          <Tab
                            eventKey="layout"
                            title={<TabLabel icon={FaPaintBrush} label="Appearance" />}
                          >
                            <Card className="border-0 editor-content-card">
                              <Card.Body className="p-4 p-lg-4">
                                <Form>
                                  <SectionDivider title="Heading" first />

                                  <Form.Group className="mb-3">
                                    <FormLabel>Heading Text</FormLabel>
                                    <Form.Control
                                      value={branding.title}
                                      onChange={(e) =>
                                        setBranding({
                                          ...branding,
                                          title: e.target.value,
                                        })
                                      }
                                    />
                                  </Form.Group>

                                  <Row className="mb-3 g-3">
                                    <Col md={6}>
                                      <FormLabel>
                                        Font Size ({branding.titleSize}rem)
                                      </FormLabel>
                                      <Form.Range
                                        min={1}
                                        max={5}
                                        step={0.1}
                                        value={branding.titleSize}
                                        onChange={(e) =>
                                          handleRangeChange("titleSize", e)
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <FormLabel>Title Color</FormLabel>
                                      <div className="color-field-shell">
                                        <InputColor
                                          initialValue={branding.titleColor}
                                          onChange={(color) =>
                                            handleColorChange("titleColor", color)
                                          }
                                          placement="right"
                                        />
                                      </div>
                                    </Col>
                                  </Row>

                                  <SectionDivider title="Subtitle" />

                                  <Form.Group className="mb-3">
                                    <FormLabel>Subtitle Text</FormLabel>
                                    <Form.Control
                                      value={branding.subtitle}
                                      onChange={(e) =>
                                        setBranding({
                                          ...branding,
                                          subtitle: e.target.value,
                                        })
                                      }
                                    />
                                  </Form.Group>

                                  <Row className="mb-3 g-3">
                                    <Col md={6}>
                                      <FormLabel>
                                        Font Size ({branding.subtitleSize}rem)
                                      </FormLabel>
                                      <Form.Range
                                        min={0.8}
                                        max={2.5}
                                        step={0.1}
                                        value={branding.subtitleSize}
                                        onChange={(e) =>
                                          handleRangeChange("subtitleSize", e)
                                        }
                                      />
                                    </Col>
                                    <Col md={6}>
                                      <FormLabel>Subtitle Color</FormLabel>
                                      <div className="color-field-shell">
                                        <InputColor
                                          initialValue={branding.subtitleColor}
                                          onChange={(color) =>
                                            handleColorChange("subtitleColor", color)
                                          }
                                          placement="right"
                                        />
                                      </div>
                                    </Col>
                                  </Row>

                                  <SectionDivider title="Brand Colors" />

                                  <Form.Group className="mb-3">
                                    <FormLabel>Primary Brand Color</FormLabel>
                                    <div className="color-field-shell">
                                      <InputColor
                                        initialValue={branding.themeColor}
                                        onChange={(color) =>
                                          handleColorChange("themeColor", color)
                                        }
                                        placement="right"
                                      />
                                    </div>
                                    <Form.Text className="text-muted">
                                      Used for buttons, focus accents and CTA styling.
                                    </Form.Text>
                                  </Form.Group>

                                  <SectionDivider title="Background" />

                                  <Form.Group className="mb-3">
                                    <FormLabel>Background Type</FormLabel>
                                    <Form.Select
                                      value={branding.backgroundType}
                                      onChange={(e) =>
                                        setBranding({
                                          ...branding,
                                          backgroundType: e.target.value,
                                        })
                                      }
                                    >
                                      <option value="color">Solid Color</option>
                                      <option value="image">Image</option>
                                      <option value="gradient">Gradient</option>
                                    </Form.Select>
                                  </Form.Group>

                                  {branding.backgroundType === "image" && (
                                    <Form.Group className="mb-3">
                                      <FormLabel>
                                        Image Overlay Opacity ({branding.imageOpacity})
                                      </FormLabel>
                                      <Form.Range
                                        min={0}
                                        max={1}
                                        step={0.05}
                                        value={branding.imageOpacity}
                                        onChange={(e) =>
                                          handleRangeChange("imageOpacity", e)
                                        }
                                      />
                                      <Form.Text className="text-muted">
                                        Controls text readability over the hero image.
                                      </Form.Text>
                                    </Form.Group>
                                  )}

                                  {branding.backgroundType === "color" && (
                                    <Form.Group className="mb-3">
                                      <FormLabel>Background Color</FormLabel>
                                      <div className="color-field-shell">
                                        <InputColor
                                          initialValue={branding.backgroundValue}
                                          onChange={(color) =>
                                            setBranding({
                                              ...branding,
                                              backgroundValue: color.hex,
                                            })
                                          }
                                          placement="right"
                                        />
                                      </div>
                                    </Form.Group>
                                  )}

                                  {branding.backgroundType === "gradient" && (
                                    <Row className="mb-3 g-3">
                                      <Col md={6}>
                                        <FormLabel>Gradient Start</FormLabel>
                                        <div className="color-field-shell">
                                          <InputColor
                                            initialValue={branding.gradientStart}
                                            onChange={(color) =>
                                              handleColorChange("gradientStart", color)
                                            }
                                          />
                                        </div>
                                      </Col>
                                      <Col md={6}>
                                        <FormLabel>Gradient End</FormLabel>
                                        <div className="color-field-shell">
                                          <InputColor
                                            initialValue={branding.gradientEnd}
                                            onChange={(color) =>
                                              handleColorChange("gradientEnd", color)
                                            }
                                          />
                                        </div>
                                      </Col>
                                    </Row>
                                  )}

                                  <SectionDivider title="Logo" />

                                  <Form.Group className="mb-3">
                                    <Form.Check
                                      type="switch"
                                      label="Show Logo in hero"
                                      checked={branding.showLogo}
                                      onChange={(e) =>
                                        setBranding({
                                          ...branding,
                                          showLogo: e.target.checked,
                                        })
                                      }
                                    />
                                  </Form.Group>

                                  {branding.showLogo && (
                                    <>
                                      <Form.Group className="mb-3">
                                        <FormLabel>Logo URL</FormLabel>
                                        <Form.Control
                                          value={branding.logoUrl}
                                          onChange={(e) =>
                                            setBranding({
                                              ...branding,
                                              logoUrl: e.target.value,
                                            })
                                          }
                                          placeholder="https://... or relative CDN path"
                                        />
                                        <Form.Text className="text-muted">
                                          Leave blank to use the default uploaded logo.
                                        </Form.Text>
                                      </Form.Group>

                                      <Form.Group className="mb-3">
                                        <FormLabel>Light Logo URL (optional)</FormLabel>
                                        <Form.Control
                                          value={branding.lightLogoUrl}
                                          onChange={(e) =>
                                            setBranding({
                                              ...branding,
                                              lightLogoUrl: e.target.value,
                                            })
                                          }
                                          placeholder="Optional alternate logo for dark backgrounds"
                                        />
                                      </Form.Group>

                                      <Row className="mb-3 g-3">
                                        <Col sm={6}>
                                          <Form.Group>
                                            <FormLabel>
                                              Logo Width (px)
                                              <span className="text-muted fw-normal ms-1">
                                                (80–400)
                                              </span>
                                            </FormLabel>
                                            <Form.Control
                                              type="number"
                                              value={branding.logoWidth}
                                              onChange={handleNumericChange(
                                                "logoWidth",
                                                LOGO_WIDTH_MIN,
                                                LOGO_WIDTH_MAX
                                              )}
                                            />
                                          </Form.Group>
                                        </Col>
                                        <Col sm={6}>
                                          <Form.Group>
                                            <FormLabel>
                                              Logo Height (px)
                                              <span className="text-muted fw-normal ms-1">
                                                (40–260)
                                              </span>
                                            </FormLabel>
                                            <Form.Control
                                              type="number"
                                              value={branding.logoHeight}
                                              onChange={handleNumericChange(
                                                "logoHeight",
                                                LOGO_HEIGHT_MIN,
                                                LOGO_HEIGHT_MAX
                                              )}
                                            />
                                          </Form.Group>
                                        </Col>
                                      </Row>
                                    </>
                                  )}

                                  <SectionDivider title="Display Options" />

                                  <Form.Group className="mb-1">
                                    <Form.Check
                                      type="switch"
                                      label="Show Property Info Section"
                                      checked={branding.showPropertyInfo}
                                      onChange={(e) =>
                                        setBranding({
                                          ...branding,
                                          showPropertyInfo: e.target.checked,
                                        })
                                      }
                                    />
                                  </Form.Group>
                                </Form>
                              </Card.Body>
                            </Card>
                          </Tab>

                          <Tab
                            eventKey="property-info"
                            title={<TabLabel icon={FaBuilding} label="Property Info" />}
                          >
                            <PropertyInfo
                              branding={branding}
                              setBranding={setBranding}
                              heroImageKey={heroImageKey}
                              setHeroImageKey={setHeroImageKey}
                              galleryImages={galleryImages}
                              onSave={handleSave}
                            />
                          </Tab>

                          <Tab
                            eventKey="notifications"
                            title={<TabLabel icon={FaBell} label="Notifications" />}
                          >
                            <NotificationSettings
                              branding={branding}
                              setBranding={setBranding}
                            />
                          </Tab>

                          <Tab
                            eventKey="manage-faqs"
                            title={
                              <TabLabel
                                icon={FaQuestionCircle}
                                label="Manage FAQs"
                              />
                            }
                          >
                            <ManageFaqs
                              branding={branding}
                              setBranding={setBranding}
                            />
                          </Tab>

                          <Tab
                            eventKey="seo"
                            title={<TabLabel icon={FaSearch} label="SEO" />}
                          >
                            <SeoEditor
                              branding={branding}
                              setBranding={setBranding}
                              subdomain={subdomain}
                              setSubdomain={setSubdomain}
                            />
                          </Tab>

                          <Tab
                            eventKey="ai-concierge"
                            title={
                              <TabLabel
                                icon={FaComments}
                                label="AI Concierge"
                                badge="NEW"
                              />
                            }
                          >
                            <AiBotSettingsEditor
                              branding={branding}
                              setBranding={setBranding}
                            />
                          </Tab>
                        </Tabs>
                      </div>
                    </div>

                    <div className="tabs-arrow-wrap">
                      <Button
                        variant="light"
                        className="tabs-arrow-btn"
                        onClick={() => scrollTabs("right")}
                      >
                        <FaChevronRight />
                      </Button>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {activeKey === "layout" && (
              <Col xl={4}>
                <Card className="preview-shell-card border-0">
                  <div className="preview-shell-head">
                    <div>
                      <div className="preview-label">Live Preview</div>
                      <div className="preview-title">
                        Guest-facing booking page
                      </div>
                    </div>
                    <StatusPill tone="info" text="Scaled preview" />
                  </div>

                  <div className="preview-shell-body">
                    <div className="preview-browser">
                      <div className="preview-browser-top">
                        <span className="dot red" />
                        <span className="dot yellow" />
                        <span className="dot green" />
                        <div className="address-bar">
                          https://{subdomain || "your-subdomain"}.netshine.one
                        </div>
                      </div>

                      <div className="preview-canvas">
                        <div className="preview-page">
                          <Card
                            className="p-0 text-center position-relative preview-hero-card"
                            style={{
                              ...getBackground(),
                              minHeight: "292px",
                              borderRadius: 0,
                              overflow: "hidden",
                            }}
                          >
                            {branding.backgroundType === "image" && (
                              <>
                                {renderImagePreviewControls()}

                                {uploading && (
                                  <div className="preview-upload-overlay">
                                    <Spinner animation="border" />
                                  </div>
                                )}
                              </>
                            )}

                            {branding.showLogo && (
                              <img
                                src={branding.logoUrl || undefined}
                                alt="logo"
                                style={{
                                  position: "absolute",
                                  top: "14px",
                                  left: "16px",
                                  zIndex: 2,
                                  width: `${previewScale.logoWidth}px`,
                                  height: `${previewScale.logoHeight}px`,
                                  objectFit: "contain",
                                }}
                              />
                            )}

                            <div
                              className="text-center position-relative"
                              style={{
                                zIndex: 2,
                                padding: "58px 16px 24px",
                                maxWidth: "100%",
                              }}
                            >
                              <h2
                                style={{
                                  fontSize: `${previewScale.titleSize}rem`,
                                  color: branding.titleColor,
                                  lineHeight: 1.18,
                                  fontWeight: 700,
                                  marginBottom: 8,
                                }}
                              >
                                {branding.title}
                              </h2>

                              <p
                                style={{
                                  fontSize: `${previewScale.subtitleSize}rem`,
                                  color: branding.subtitleColor,
                                  maxWidth: 410,
                                  margin: "0 auto",
                                  lineHeight: 1.42,
                                }}
                              >
                                {branding.subtitle}
                              </p>

                              <div className="preview-search-card mt-3">
                                <div className="preview-search-left">
                                  <div className="preview-search-group">
                                    <Calendar size={14} />
                                    <span>Nov 6, 2025 – Nov 7, 2025</span>
                                  </div>
                                  <div className="preview-search-group">
                                    <Users size={14} />
                                    <span>2 adults · 0 children · 1 room</span>
                                  </div>
                                </div>

                                <Button
                                  className="preview-search-btn"
                                  style={{
                                    backgroundColor: branding.themeColor,
                                    borderColor: branding.themeColor,
                                  }}
                                >
                                  {branding.buttonText}
                                </Button>
                              </div>
                            </div>
                          </Card>

                          {branding.showPropertyInfo && (
                            <div className="preview-content-card">
                              <div className="preview-content-title">
                                Property Overview
                              </div>

                              {branding.propertyOverview ? (
                                <div
                                  className="preview-content-text"
                                  dangerouslySetInnerHTML={{
                                    __html: branding.propertyOverview,
                                  }}
                                />
                              ) : (
                                <p className="preview-content-empty">
                                  Add your property overview in the Property Info
                                  tab to preview it here.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </div>
    </div>
  );
};

const SectionDivider = ({ title, first }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      marginBottom: 14,
      ...(!first
        ? { marginTop: 12, paddingTop: 18, borderTop: "1px solid #eef2f7" }
        : {}),
    }}
  >
    {title}
  </div>
);

const FormLabel = ({ children }) => (
  <Form.Label
    style={{
      fontSize: 13,
      fontWeight: 600,
      color: "#334155",
      marginBottom: 6,
    }}
  >
    {children}
  </Form.Label>
);

const TabLabel = ({ icon: Icon, label, badge }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: 13.5,
      fontWeight: 600,
      whiteSpace: "nowrap",
      letterSpacing: "0.01em",
    }}
  >
    <Icon style={{ fontSize: 15, opacity: 0.82 }} />
    {label}
    {badge && (
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          padding: "2px 7px",
          borderRadius: 999,
          backgroundColor: "#eef2ff",
          color: "#4f46e5",
          letterSpacing: "0.04em",
          textTransform: "uppercase",
          lineHeight: "14px",
        }}
      >
        {badge}
      </span>
    )}
  </span>
);

const StatusPill = ({ tone = "info", text }) => {
  const map = {
    success: {
      bg: "#ecfdf3",
      color: "#027a48",
      border: "#abefc6",
    },
    warning: {
      bg: "#fffaeb",
      color: "#b54708",
      border: "#fedf89",
    },
    info: {
      bg: "#eff8ff",
      color: "#175cd3",
      border: "#b2ddff",
    },
  };

  const s = map[tone] || map.info;

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 10px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
      }}
    >
      {text}
    </span>
  );
};

const pageStyles = `
.booking-engine-admin-shell {
  background:
    radial-gradient(circle at top left, rgba(79, 70, 229, 0.045), transparent 24%),
    linear-gradient(180deg, #f8fafc 0%, #f3f6fb 100%);
  min-height: 100%;
  padding-top: 20px;
  padding-bottom: 24px;
}

.booking-admin-container {
  max-width: 1680px;
  margin: 0 auto;
  padding-left: 20px !important;
  padding-right: 20px !important;
}

.page-head-wrap {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 18px;
}

.page-kicker {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 8px;
}

.page-title {
  font-size: 1.8rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 6px;
}

.page-subtitle {
  font-size: 14px;
  color: #64748b;
}

.head-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.head-save-btn {
  min-width: 144px;
}

.enterprise-alert {
  border-radius: 18px !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
  margin-bottom: 24px !important;
}

.enterprise-alert-success {
  background: linear-gradient(180deg, #f3fff7 0%, #edf9f1 100%) !important;
}

.enterprise-alert-warning {
  background: linear-gradient(180deg, #fffdf7 0%, #fffaef 100%) !important;
}

.alert-content-block {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  width: 100%;
  flex-wrap: wrap;
}

.alert-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 2px;
}

.alert-subtitle {
  font-size: 13px;
  color: #64748b;
}

.empty-state-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  padding: 56px 24px;
  text-align: center;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.05);
}

.empty-state-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: linear-gradient(135deg, #eef2ff, #f8fafc);
  color: #4f46e5;
  font-size: 22px;
}

.editor-shell-card,
.preview-shell-card {
  border-radius: 22px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
  border: 1px solid #e9eef5;
}

.editor-top-tabs {
  display: flex;
  align-items: stretch;
  gap: 6px;
  padding: 10px 10px 0;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fcfdff 0%, #f8fafc 100%);
}

.tabs-arrow-wrap {
  display: flex;
  align-items: center;
  padding-bottom: 10px;
}

.tabs-arrow-btn {
  width: 34px;
  height: 34px;
  border-radius: 10px !important;
  border: 1px solid #e5e7eb !important;
  display: grid !important;
  place-items: center;
  color: #475569 !important;
  background: #fff !important;
  box-shadow: none !important;
}

.tabs-scroll-region {
  flex: 1;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.tabs-scroll-region::-webkit-scrollbar {
  display: none;
}

.enterprise-tabs .nav-tabs {
  flex-wrap: nowrap !important;
  border-bottom: none;
  gap: 6px;
  min-width: max-content;
  white-space: nowrap;
  padding-bottom: 10px;
}

.enterprise-tabs .nav-tabs .nav-item {
  flex: 0 0 auto;
}

.enterprise-tabs .nav-tabs .nav-link {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #475569;
  border-radius: 12px;
  padding: 10px 14px;
  min-height: 46px;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
}

.enterprise-tabs .nav-tabs .nav-link:hover {
  background: #f8fafc;
  border-color: #dbe3ee;
  color: #0f172a;
}

.enterprise-tabs .nav-tabs .nav-link.active {
  color: #312e81;
  background: linear-gradient(180deg, #eef2ff 0%, #f7f8ff 100%);
  border-color: #c7d2fe;
  box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.08);
}

.editor-content-card {
  border-radius: 0 0 18px 18px;
}

.color-field-shell {
  display: inline-flex;
  align-items: center;
  min-height: 42px;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}

.preview-shell-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 18px 20px;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #fcfdff 0%, #f8fafc 100%);
}

.preview-label {
  font-size: 12px;
  font-weight: 800;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 4px;
}

.preview-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.preview-shell-body {
  padding: 14px;
  background: #f8fafc;
}

.preview-browser {
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #dbe3ee;
  background: #fff;
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.08);
}

.preview-browser-top {
  display: flex;
  align-items: center;
  gap: 8px;
  height: 44px;
  padding: 0 14px;
  background: #eef2f6;
  border-bottom: 1px solid #dde6ef;
}

.preview-browser-top .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.preview-browser-top .dot.red { background: #ff5f57; }
.preview-browser-top .dot.yellow { background: #febc2e; }
.preview-browser-top .dot.green { background: #28c840; }

.address-bar {
  margin-left: 10px;
  height: 28px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #dde6ef;
  color: #64748b;
  font-size: 12px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: 1;
}

.preview-canvas {
  background: linear-gradient(180deg, #f8fafc 0%, #edf2f7 100%);
  padding: 12px;
}

.preview-page {
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.08);
}

.preview-hero-card {
  position: relative;
}

.preview-upload-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.58);
  z-index: 10;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-actions {
  position: absolute;
  right: 12px;
  top: 12px;
  z-index: 5;
  display: flex;
  gap: 8px;
}

.preview-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.12);
  color: #334155;
  border: 1px solid rgba(226, 232, 240, 0.95);
}

.preview-search-card {
  width: min(94%, 500px);
  margin-left: auto;
  margin-right: auto;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.10);
  padding: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  text-align: left;
  flex-wrap: wrap;
}

.preview-search-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  flex: 1;
}

.preview-search-group {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  font-size: 12px;
  min-width: 0;
}

.preview-search-group span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.preview-search-btn {
  min-width: 132px;
  border-radius: 999px !important;
  font-size: 12px !important;
  font-weight: 700 !important;
  padding: 8px 14px !important;
  white-space: nowrap;
}

.preview-content-card {
  padding: 16px 16px 18px;
  background: #fff;
}

.preview-content-title {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 10px;
}

.preview-content-text {
  font-size: 12.5px;
  line-height: 1.65;
  color: #475569;
}

.preview-content-empty {
  font-size: 12.5px;
  color: #94a3b8;
  margin-bottom: 0;
}

@media (max-width: 1399.98px) {
  .preview-search-card {
    width: min(95%, 480px);
  }
}

@media (max-width: 991.98px) {
  .booking-admin-container {
    padding-left: 16px !important;
    padding-right: 16px !important;
  }

  .page-title {
    font-size: 1.45rem;
  }

  .preview-shell-body {
    padding: 12px;
  }

  .preview-canvas {
    padding: 10px;
  }
}

@media (max-width: 767.98px) {
  .booking-engine-admin-shell {
    padding-top: 14px;
  }

  .editor-top-tabs {
    gap: 6px;
    padding: 10px 10px 0;
  }

  .enterprise-tabs .nav-tabs .nav-link {
    padding: 10px 13px;
  }

  .preview-search-card {
    padding: 10px;
  }

  .preview-search-btn {
    width: 100%;
  }
}
`;

export default ManageBookingEngine;